import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getSingleNode } from '../services/operations/nodeAPI';
import { getWorksheetsByNode } from '../services/operations/worksheetAPI';
import { exportAsPDF, exportAsWord, exportAsExcel } from '../services/operations/exportUtils';
import { 
  Download, 
  FileText, 
  Users, 
  ArrowLeft, 
  Loader2,
  FileSpreadsheet,
  FileImage
} from 'lucide-react';

const DownloadPage = () => {
  const { nodeNumber } = useParams();
  const [node, setNode] = useState();
  const [drawingsheet, setDrawingsheet] = useState(null);
  const [nodeData, setNodeData] = useState(null);
  const [worksheetData, setWorksheetData] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // Fetch node details
        const nodeResponse = await getSingleNode(nodeNumber);
        setDrawingsheet(nodeResponse.drawingSheet);
        setNodeData(nodeResponse.data);

        // Fetch worksheets for the node
        const worksheetResponse = await getWorksheetsByNode(nodeNumber);
        setNode(worksheetResponse.nodeNumber);
        setWorksheetData(worksheetResponse.data || []);
      } catch (err) {
        console.error('Error fetching download data:', err);
        alert('Could not fetch data for download');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [nodeNumber]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50/30 py-8 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Modern Header */}
        <div className="text-center mb-6">
          <div className="flex justify-center items-center gap-2">
            <div className="p-2 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl shadow-xl">
              <Download className="h-6 w-6 text-white" />
            </div>
          
          <h1 className="text-2xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent ">
            Download Files for Node {node}
          </h1>
          </div>
          <p className="text-slate-600 text-lg">Export your HAZOP analysis in multiple formats</p>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-20">
            <div className="text-center">
              <Loader2 className="animate-spin h-6 w-6 text-blue-500 mx-auto mb-4" />
              <p className="text-slate-600 text-lg">Loading data...</p>
            </div>
          </div>
        ) : nodeData ? (
          <>
            {/* Node Information Card */}
            <div className="relative overflow-hidden mb-8">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-indigo-600/5 rounded-2xl"></div>
              <div className="relative bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl shadow-lg">
                    <FileText className="h-6 w-6 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-slate-800">Node Details</h3>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <div className="p-4 bg-blue-50/80 backdrop-blur-sm rounded-xl border border-blue-200/50">
                    <p className="text-sm font-semibold text-blue-700 uppercase tracking-wider mb-1">Node Number</p>
                    <p className="text-lg font-bold text-slate-800">{nodeData.nodeNumber}</p>
                  </div>
                  
                  <div className="p-4 bg-emerald-50/80 backdrop-blur-sm rounded-xl border border-emerald-200/50">
                    <p className="text-sm font-semibold text-emerald-700 uppercase tracking-wider mb-1">Drawing Number</p>
                    <p className="text-lg font-bold text-slate-800">{nodeData.drawingNumber}</p>
                  </div>
                  
                  <div className="p-4 bg-purple-50/80 backdrop-blur-sm rounded-xl border border-purple-200/50">
                    <p className="text-sm font-semibold text-purple-700 uppercase tracking-wider mb-1">Design Intent</p>
                    <p className="text-slate-800 font-medium">{nodeData.designIntent}</p>
                  </div>
                  
                  <div className="p-4 bg-amber-50/80 backdrop-blur-sm rounded-xl border border-amber-200/50">
                    <p className="text-sm font-semibold text-amber-700 uppercase tracking-wider mb-1">Chairman</p>
                    <p className="text-slate-800 font-medium">{nodeData.chairman.firstName} {nodeData.chairman.lastName}</p>
                  </div>
                  
                  <div className="p-4 bg-indigo-50/80 backdrop-blur-sm rounded-xl border border-indigo-200/50">
                    <p className="text-sm font-semibold text-indigo-700 uppercase tracking-wider mb-1">Scribe</p>
                    <p className="text-slate-800 font-medium">{nodeData.scribe.firstName} {nodeData.scribe.lastName}</p>
                  </div>
                  
                  <div className="p-4 bg-pink-50/80 backdrop-blur-sm rounded-xl border border-pink-200/50">
                    <p className="text-sm font-semibold text-pink-700 uppercase tracking-wider mb-1">Team</p>
                    <p className="text-slate-800 font-medium">{nodeData.team.map((member) => member.firstName + " " + member.lastName).join(", ")}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Worksheet Table */}
            <div className="relative overflow-hidden mb-8">
              <div className="absolute inset-0 bg-gradient-to-br from-slate-500/5 to-gray-600/5 rounded-2xl"></div>
              <div className="relative bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 overflow-hidden">
                <div className="p-6 border-b border-white/20 bg-gradient-to-r from-slate-50/50 to-white/50">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-gradient-to-br from-slate-600 to-gray-700 rounded-xl shadow-lg">
                      <FileSpreadsheet className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-slate-800">Worksheets</h3>
                      <p className="text-slate-600 text-sm mt-1">HAZOP analysis worksheets for this node</p>
                    </div>
                  </div>
                </div>
                
                {worksheetData.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white">
                        <tr>
                          <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider">Serial No</th>
                          <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider">Guide Word</th>
                          <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider">Deviation</th>
                          <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider">Causes</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-white/20">
                        {worksheetData.map((worksheet, index) => (
                          <tr key={worksheet._id} className="hover:bg-white/50 transition-colors duration-200">
                            <td className="px-6 py-4 text-center font-semibold text-slate-800">{index + 1}</td>
                            <td className="px-6 py-4 text-slate-700 font-medium">{worksheet.guideWordParameter || "N/A"}</td>
                            <td className="px-6 py-4 text-slate-700">{worksheet.deviation || "N/A"}</td>
                            <td className="px-6 py-4 text-slate-700">
                              {Array.isArray(worksheet.causes) && worksheet.causes.length > 0
                                ? worksheet.causes.join(', ')
                                : "N/A"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-16">
                    <div className="p-6 bg-slate-100 rounded-2xl w-fit mx-auto mb-6">
                      <FileSpreadsheet className="h-16 w-16 text-slate-400 mx-auto" />
                    </div>
                    <p className="text-slate-500 text-xl">No worksheets found.</p>
                  </div>
                )}
              </div>
            </div>

            {/* Download Buttons */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <button
                onClick={() => exportAsPDF(nodeData, worksheetData)}
                className="group relative overflow-hidden bg-gradient-to-r from-red-500 to-pink-600 hover:from-red-600 hover:to-pink-700 text-white py-6 px-8 rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105"
              >
                <div className="flex items-center justify-center gap-3">
                  <FileImage className="h-6 w-6" />
                  <span className="font-bold text-lg">Download as PDF</span>
                </div>
              </button>
              
              <button
                onClick={() => exportAsWord(nodeData, worksheetData)}
                className="group relative overflow-hidden bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white py-6 px-8 rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105"
              >
                <div className="flex items-center justify-center gap-3">
                  <FileText className="h-6 w-6" />
                  <span className="font-bold text-lg">Download as Word</span>
                </div>
              </button>
              
              <button
                onClick={() => exportAsExcel(nodeData, worksheetData)}
                className="group relative overflow-hidden bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 text-white py-6 px-8 rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105"
              >
                <div className="flex items-center justify-center gap-3">
                  <FileSpreadsheet className="h-6 w-6" />
                  <span className="font-bold text-lg">Download as Excel</span>
                </div>
              </button>
            </div>
          </>
        ) : (
          <div className="text-center py-20">
            <div className="p-6 bg-slate-100 rounded-2xl w-fit mx-auto mb-6">
              <FileText className="h-16 w-16 text-slate-400 mx-auto" />
            </div>
            <p className="text-slate-500 text-xl">Node data not found.</p>
          </div>
        )}

        {/* Back Button */}
        <button
          onClick={() => navigate(-1)}
          className="w-full bg-gradient-to-r from-slate-600 to-gray-700 hover:from-slate-700 hover:to-gray-800 text-white py-4 px-6 rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 flex items-center justify-center gap-3 font-bold text-lg transform hover:scale-[1.02]"
        >
          <ArrowLeft className="h-6 w-6" />
          Go Back
        </button>
      </div>
    </div>
  );
};

export default DownloadPage;